<template>
  <div class="lang-switch-wrapper" ref="wrapperRef">
    <button class="lang-btn" @click="toggleDropdown">
      <i-local-language class="lang-icon" />
      <span class="lang-btn-text">
        <template v-if="isMobile">{{ $t('common.selectLanguage') }}</template>
      </span>
      <span class="current-lang">{{ langLabel }}</span>
    </button>
    <transition :name="isMobile ? 'dropdown' : 'dropdown-pc'">
      <div v-if="showDropdown" class="lang-dropdown">
        <div
          v-for="lang in languages"
          :key="lang.value"
          class="lang-option"
          :class="{ active: locale === lang.value && isMobile }"
          @click="selectLang(lang.value)"
        >
          <span>{{ lang.label }}</span>
          <i-local-check-mark v-if="locale === lang.value" class="check-icon" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
const { locale, t } = useI18n()

const props = defineProps({
  isMobile: { type: Boolean, default: false },
  show: { type: Boolean, default: false }
})

const emit = defineEmits(['toggle', 'select'])

const wrapperRef = ref(null)
const showDropdown = ref(props.show)

const languages = [
  { value: 'zh', label: computed(() => t('language.zh')) },
  { value: 'en', label: computed(() => t('language.en')) },
  { value: 'th', label: computed(() => t('language.th')) }
]

const langLabel = computed(() => {
  const lang = languages.find((l) => l.value === locale.value)
  return lang ? lang.label : ''
})

const toggleDropdown = () => {
  emit('toggle')
}

const selectLang = (lang) => {
  emit('select', lang)
}

watch(
  () => props.show,
  (val) => {
    showDropdown.value = val
  }
)

defineExpose({
  wrapperRef
})
</script>

<style lang="scss" scoped>
.lang-switch-wrapper {
  position: relative;

  .lang-btn {
    display: flex;
    align-items: center;
    @include adapt($prop: gap, $h5: 16, $pc: 17);
    @include adapt($prop: width, $h5: 100%, $pc: auto);
    @include adapt($prop: height, $h5: 60, $pc: auto);
    @include adapt($prop: padding, $h5: 0 36 0 20, $pc: 8 16);
    @include adapt(
      $prop: background,
      $h5: rgba(255, 255, 255, 0.1),
      $pc: transparent
    );
    @include adapt($prop: border-radius, $h5: 20, $pc: 0);
    @include adapt($prop: font-size, $h5: 28, $pc: 20);
    border: none;
    color: #fff;
    cursor: pointer;

    .lang-icon {
      @include adapt($prop: width, $h5: 52, $pc: 26);
      @include adapt($prop: height, $h5: 52, $pc: 26);
    }

    .current-lang {
      @include adapt($prop: font-size, $h5: 28, $pc: 20);
      @include respond-down('md') {
        margin-left: auto;
      }
    }
  }

  .lang-dropdown {
    @include respond-down('md') {
      margin-top: h5(20);
      padding: h5(8);
      background: rgba(255, 255, 255, 0.1);
      border-radius: h5(20);
      overflow: hidden;
    }

    @include respond-up('md') {
      position: absolute;
      top: calc(100% + #{pc(15)});
      right: 50%;
      transform: translateX(50%);
      width: pc(150);
      padding: pc(30) pc(10);
      border-radius: pc(24);
      background: linear-gradient(
        180deg,
        rgba(66, 33, 125, 1) 0%,
        rgba(60, 15, 83, 1) 100%
      );
      box-shadow:
        0 pc(8) pc(30) rgba(0, 0, 0, 0.6),
        0 0 pc(20) rgba(138, 43, 226, 0.3);

      &::before {
        content: '';
        position: absolute;
        top: #{pc(-14)};
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: pc(14) solid transparent;
        border-right: pc(14) solid transparent;
        border-bottom: pc(20) solid rgba(66, 33, 125, 1);
        filter: drop-shadow(0 -pc(2) pc(2) rgba(0, 0, 0, 0.3));
        z-index: 2;
      }

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: pc(24);
        padding: pc(3);
        background: linear-gradient(
          135deg,
          rgba(180, 160, 200, 0.9) 0%,
          rgba(80, 60, 100, 0.3) 30%,
          rgba(40, 25, 60, 0.1) 50%,
          rgba(80, 60, 100, 0.3) 70%,
          rgba(180, 160, 200, 0.9) 100%
        );
        -webkit-mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
        z-index: 1;
      }
    }

    .lang-option {
      display: flex;
      justify-content: space-between;
      align-items: center;
      @include respond-down('md') {
        width: 100%;
        height: h5(53);
        padding: 0 h5(13) 0 h5(38);
        border-radius: h5(15);
        font-size: h5(28);
        cursor: pointer;

        &.active {
          background: rgba(139, 147, 178, 1);
        }
      }

      @include respond-up('md') {
        padding: pc(14) pc(6);
        font-size: pc(20);
        cursor: pointer;
        border-radius: pc(10);
        transition: background 0.2s ease;
        color: #fff;

        &:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        &:not(:last-child) {
          border-bottom: pc(1) solid rgba(76, 43, 113, 0.9);
        }
      }

      .check-icon {
        @include adapt($prop: width, $h5: 49, $pc: 26);
        @include adapt($prop: height, $h5: 49, $pc: 26);
        @include respond-up('md') {
          margin-left: pc(10);
        }
      }
    }
  }
}
</style>
